/**
 * AI:
 * Status: 'confirmed'
 * Type: Component
 * SubType: Presentational
 * Reason: Raw-data tab — pretty-prints the record's raw JSON payload with
 *         copy-to-clipboard and download actions.
 */
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    SimpleChanges
} from '@angular/core';
import {EiImportRecordDto} from '../../../../../../../../model/dto/entity/ei-import-record.dto';
import {MvsMessageService} from '../../../../../../../../../../core/shared/service/message/mvs-message.service';

@Component({
    selector: 'ei-tooling-raw-data-tab',
    templateUrl: './ei-tooling-raw-data-tab.component.html',
    styleUrls: ['./ei-tooling-raw-data-tab.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EiToolingRawDataTabComponent implements OnChanges {

    @Input() record: EiImportRecordDto | null = null;

    formattedJson: string = '';
    hasData: boolean = false;

    constructor(
        private readonly messageService: MvsMessageService,
        private readonly cdr: ChangeDetectorRef,
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['record']) {
            this.rebuildFormattedJson();
        }
    }

    /**
     * Pretty-prints the record's JSON payload, falling back to raw text if parsing fails.
     */
    private rebuildFormattedJson(): void {
        const raw = this.record?.recordJson;
        if (!raw) {
            this.formattedJson = '';
            this.hasData = false;
            this.cdr.markForCheck();
            return;
        }
        try {
            const parsed = JSON.parse(raw);
            this.formattedJson = JSON.stringify(parsed, null, 2);
        } catch (_) {
            this.formattedJson = raw;
        }
        this.hasData = true;
        this.cdr.markForCheck();
    }

    /**
     * Copies the formatted JSON to the system clipboard.
     */
    copyToClipboard(): void {
        if (!this.formattedJson) {
            return;
        }
        if (!navigator?.clipboard) {
            this.messageService.showWarningMessage(
                'Nicht unterstützt',
                'Die Zwischenablage-API wird vom Browser nicht unterstützt.'
            );
            return;
        }
        navigator.clipboard.writeText(this.formattedJson)
            .then(() => this.messageService.showSuccessMessage(
                'Kopiert',
                'Die Rohdaten wurden in die Zwischenablage kopiert.'
            ))
            .catch(() => this.messageService.showErrorMessage(
                'Fehler',
                'Kopieren in die Zwischenablage fehlgeschlagen.'
            ));
    }

    /**
     * Downloads the formatted JSON as a file.
     */
    downloadJson(): void {
        if (!this.formattedJson) {
            return;
        }
        const filename = 'ei-import-record-' + (this.record?.id ?? 'unknown') + '.json';
        const blob = new Blob([this.formattedJson], {type: 'application/json;charset=utf-8'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
}
